import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContextV3';
import { roleAllowed } from './utils/roles';
import { requiresSchoolSetup } from './utils/schoolSetup';
import { requiresOrganizationSetup } from './utils/organizationSetup';
import ErrorBoundary from './components/ErrorBoundary';
import NotificationProvider from './components/common/NotificationProvider';

// Lazy load components for code splitting
const PublicCampaignPage = lazy(() => import('./pages/PublicCampaignPage'));
const UniversalEntry = lazy(() => import('./components/auth/UniversalEntry'));
const MagicLinkCallback = lazy(() => import('./components/auth/MagicLinkCallback'));
const InvalidLinkPage = lazy(() => import('./components/auth/InvalidLinkPage'));
const OnboardingWizard = lazy(() => import('./components/auth/OnboardingWizard'));
const JoinSchool = lazy(() => import('./components/auth/JoinSchool'));
const PendingSchoolAccess = lazy(() => import('./components/auth/PendingSchoolAccess'));

// Public pages (restored from v2 public routes)
const HomePage = lazy(() => import('./pages/HomePage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));

// Business modules (kept from v2)
const Dashboard = lazy(() => import('./components/Dashboard'));
const Campaigns = lazy(() => import('./components/campaign/CampaignListV2'));
const CampaignForm = lazy(() => import('./components/campaign/CampaignFormV2'));
const CampaignWorkspace = lazy(() => import('./components/campaign/CampaignWorkspaceV2'));
const Members = lazy(() => import('./components/members/MembersPage'));
const Verification = lazy(() => import('./components/MemberVerificationPage'));
const Orders = lazy(() => import('./components/Orders'));
const SchoolReports = lazy(() => import('./components/SchoolReports'));
const OrganizationSettings = lazy(() => import('./components/organization/OrganizationSettings'));
const OrganizationLanding = lazy(() => import('./components/organization/OrganizationLanding'));
const OrganizationLogin = lazy(() => import('./components/organization/OrganizationLogin'));
const OrganizationSuperAdminLoginPage = lazy(() => import('./components/organization/OrganizationSuperAdminLoginPage'));
const OrganizationPending = lazy(() => import('./components/organization/OrganizationPending'));
const OrganizationOnboarding = lazy(() => import('./components/organization/OrganizationOnboarding'));
const ItemListV2 = lazy(() => import('./components/campaign/ItemListV2'));
const ItemFormV2 = lazy(() => import('./components/campaign/ItemFormV2'));
const Layout = lazy(() => import('./components/Layout'));
const SchoolOnboardingWizard = lazy(() => import('./components/SchoolOnboardingWizard'));

// Role dashboards (real implementations, reused from v2)
const ParentDashboard = lazy(() => import('./pages/ParentDashboard'));
const VendorShell = lazy(() => import('./components/vendor/VendorShell'));
const VendorMagicLinkVerify = lazy(() => import('./components/vendor/VendorMagicLinkVerify'));
const VendorProtectedRoute = lazy(() => import('./components/vendor/VendorProtectedRoute'));
const VendorDashboardPage = lazy(() => import('./components/vendor/VendorDashboard'));
const VendorRFQsOpen = lazy(() => import('./components/vendor/VendorRFQsOpen'));
const VendorSubmittedQuotations = lazy(() => import('./components/vendor/VendorSubmittedQuotations'));
const VendorAwardedJobs = lazy(() => import('./components/vendor/VendorAwardedJobs'));
const VendorProduction = lazy(() => import('./components/vendor/VendorProduction'));
const VendorPrinting = lazy(() => import('./components/vendor/VendorPrinting'));
const VendorCompleted = lazy(() => import('./components/vendor/VendorCompleted'));
const VendorProfile = lazy(() => import('./components/vendor/VendorProfile'));
const ProcurementRFQs = lazy(() => import('./components/school-platform/ProcurementRFQs'));
const ProcurementVendors = lazy(() => import('./components/school-platform/ProcurementVendors'));
const ProcurementQuotations = lazy(() => import('./components/school-platform/ProcurementQuotations'));
const ProcurementAwardedJobs = lazy(() => import('./components/school-platform/ProcurementAwardedJobs'));
const ProcurementProduction = lazy(() => import('./components/school-platform/ProcurementProduction'));
const ProcurementPrinting = lazy(() => import('./components/school-platform/ProcurementPrinting'));
const ProcurementCompleted = lazy(() => import('./components/school-platform/ProcurementCompleted'));
const ProcurementDashboard = lazy(() => import('./components/school-platform/ProcurementDashboard'));
const SuperAdminDashboard = lazy(() => import('./components/school-platform/SchoolPlatformDashboard'));
const SchoolPlatformShell = lazy(() => import('./components/school-platform/SchoolPlatformShell'));
const OrganizationSuperAdminShell = lazy(() => import('./components/organization/OrganizationSuperAdminShell'));
const OrganizationLayout = lazy(() => import('./components/organization/OrganizationLayout'));
const OrganizationOrSchoolLayout = lazy(() => import('./components/organization/OrganizationOrSchoolLayout'));
const WorkspaceSettings = lazy(() => import('./pages/WorkspaceSettings'));
const MyOrganizations = lazy(() => import('./pages/MyOrganizations'));
const OrganizationDashboard = lazy(() => import('./components/organization/OrganizationDashboard'));
const OrganizationMembers = lazy(() => import('./components/organization/OrganizationMembers'));
const OrganizationOrders = lazy(() => import('./components/organization/OrganizationOrders'));
const OrganizationReports = lazy(() => import('./components/organization/OrganizationReports'));
const SchoolPlatformOrganizations = lazy(() => import('./components/school-platform/SchoolPlatformOrganizations'));
const SuperAdminLoginPage = lazy(() => import('./components/superadmin/SuperAdminLoginPage'));
const SPApprovals = lazy(() => import('./components/school-platform/SchoolPlatformApprovals'));
const SPSchools = lazy(() => import('./components/school-platform/SchoolPlatformSchools'));
const SPSchoolDetail = lazy(() => import('./components/school-platform/SchoolPlatformSchoolDetail'));
const SPSchoolAdmins = lazy(() => import('./components/school-platform/SchoolPlatformSchoolAdmins'));
const SPParents = lazy(() => import('./components/school-platform/SchoolPlatformParents'));
const SPCampaigns = lazy(() => import('./components/school-platform/SchoolPlatformCampaigns'));
const SPCampaignReview = lazy(() => import('./components/school-platform/SchoolPlatformCampaignReview'));
const SPOrders = lazy(() => import('./components/school-platform/SchoolPlatformOrders'));
const SPPrintLabels = lazy(() => import('./components/school-platform/SchoolPlatformPrintLabels'));
const SPPayments = lazy(() => import('./components/school-platform/SchoolPlatformPayments'));
const SPReports = lazy(() => import('./components/school-platform/SchoolPlatformReports'));
const SPAuditLogs = lazy(() => import('./components/school-platform/SchoolPlatformAuditLogs'));
const SPNotifications = lazy(() => import('./components/school-platform/SchoolPlatformNotifications'));
const SPSettings = lazy(() => import('./components/school-platform/SchoolPlatformSettings'));

// Parent portal (reused from v2, reconnected)
const ParentShell = lazy(() => import('./components/parent/ParentShell'));
const ParentProductDetail = lazy(() => import('./components/parent/ParentProductDetail'));
const ParentOrder = lazy(() => import('./components/parent/ParentOrder'));
const ParentCart = lazy(() => import('./components/parent/ParentCart'));
const ParentCartReceipt = lazy(() => import('./components/parent/ParentCartReceipt'));
const ParentOrderEdit = lazy(() => import('./components/parent/ParentOrderEdit'));
const ParentReceipt = lazy(() => import('./components/parent/ParentReceipt'));
const ParentCampaigns = lazy(() => import('./components/parent/ParentCampaigns'));
const ParentProfile = lazy(() => import('./components/parent/ParentProfile'));
const ParentOrders = lazy(() => import('./components/parent/ParentOrders'));
const ParentTasks = lazy(() => import('./components/parent/TaskList'));
const PaymentCallback = lazy(() => import('./pages/PaymentCallback'));
const ExplorePage = lazy(() => import('./pages/ExplorePage'));

const LoadingFallback = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
  </div>
);

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const path = window.location.pathname;
  console.log('[ProtectedRoute]', path, { loading, isAuthenticated, user: user?.email, role: user?.role, profileComplete: user?.tenant?.name ? true : false });

  if (loading) { console.log('[NAV]', path, '->', 'LOADING'); return <LoadingFallback />; }

  if (!isAuthenticated) {
    if (path.startsWith('/superorganization')) {
      return <Navigate to="/superorganization/login" replace />;
    }
    if (path.startsWith('/superschool')) {
      return <Navigate to="/superschool/login" replace />;
    }
    return <Navigate to="/auth" replace />;
  }

  if (user?.onboarding_step === 'welcome') {
    const isOrgUser = user?.role === 'organization_admin' || user?.onboarding_type === 'organization' || path.startsWith('/organization');
    if (isOrgUser && !path.startsWith('/organization/onboarding')) {
      console.log('[NAV]', path, '->', '/organization/onboarding');
      return <Navigate to="/organization/onboarding" replace />;
    }
    if (!isOrgUser && !path.startsWith('/onboarding')) {
      console.log('[NAV]', path, '->', '/onboarding');
      return <Navigate to="/onboarding" replace />;
    }
  }

  // Org-onboarding soft accounts are granted the admin role only AFTER the
  // organisation is created and they are attached, so admit them into
  // organization_admin routes by intent while they are still soft/welcome
  // (RequireOrganizationSetup keeps funneling them into the wizard).
  const hasOrganizationAdminCapability = (allowedRoles.includes('organization_admin') && user?.is_organization_admin)
    || (allowedRoles.includes('organization_admin') && user?.onboarding_type === 'organization' && (user?.is_soft_account || user?.onboarding_step === 'welcome'));

  if (allowedRoles.length > 0 && !roleAllowed(user?.role, allowedRoles) && !hasOrganizationAdminCapability) {
    // Ecosystem boundary: an authenticated admin of the other ecosystem is
    // denied access and sent to the generic access-denied page (never rendered
    // or partially exposed). Unauthenticated users already went to a login
    // above, so this branch only handles authenticated-but-not-allowed roles.
    console.log('[NAV]', path, '->', '/unauthorized', '(role not allowed:', user?.role, ')');
    return <Navigate to="/unauthorized" replace />;
  }

  console.log('[ProtectedRoute]', path, '->', 'RENDER CHILDREN');
  return children;
};

/**
 * Legacy admin-path redirect for backward compatibility.
 *
 * - /admin/*            → school ecosystem for School Super Admin roles,
 *                         organization ecosystem for the Seer Admin role
 *                         (sub-path preserved).
 * - /organization-superadmin/* → /superorganization/* (sub-path preserved).
 *
 * These keep old bookmarks/links working while the two ecosystems now live at
 * /superschool/* and /superorganization/*.
 */
const LegacyAdminRedirect = ({ from }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingFallback />;

  const rest = location.pathname.replace(from, '') || '/';
  const isOrgRole = user?.role === 'organization_super_admin';
  const to = from === '/admin'
    ? (isOrgRole ? '/superorganization' : '/superschool')
    : '/superorganization';
  const next = `${to}${rest === '/' ? '' : rest}${location.search}${location.hash}`;

  if (next === location.pathname) {
    return <Navigate to="/unauthorized" replace />;
  }
  console.log('[NAV]', location.pathname, '->', next, '(legacy admin redirect)');
  return <Navigate to={next} replace />;
};

/**
 * Full-screen onboarding gate for School Admins.
 * When school_profile_completed is false the school portal is locked and the
 * user is funneled into the School Setup wizard (no sidebar / dashboard nav).
 * Falls back to derived profile completeness for stale cached sessions.
 */
const RequireSchoolSetup = ({ children }) => {
  const { user, loading } = useAuth();
  const path = window.location.pathname;

  if (loading) return <LoadingFallback />;
  if (!user) return children;

  if (requiresSchoolSetup(user)) {
    console.log('[NAV]', path, '->', '/school/setup (setup incomplete)');
    return <Navigate to="/school/setup" replace />;
  }

  return children;
};

/**
 * Inverse of RequireSchoolSetup: once a School Admin has completed the School
 * Profile, bounce them out of /school/setup to the dashboard so they can never
 * re-enter the setup wizard after finishing.
 */
const RedirectIfSetupComplete = ({ children }) => {
  const { user, loading } = useAuth();
  const path = window.location.pathname;

  if (loading) return <LoadingFallback />;
  if (!user) return children;

  if (!requiresSchoolSetup(user)) {
    console.log('[NAV]', path, '->', '/dashboard (setup already complete)');
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const RequireOrganizationSetup = ({ children }) => {
  const { user, loading } = useAuth();
  const path = window.location.pathname;

  if (loading) return <LoadingFallback />;
  if (!user) return children;

  if (requiresOrganizationSetup(user)) {
    console.log('[NAV]', path, '->', '/organization/onboarding (organization setup incomplete)');
    return <Navigate to="/organization/onboarding" replace />;
  }

  return children;
};

const RedirectIfOrganizationSetupComplete = ({ children }) => {
  const { user, loading } = useAuth();
  const path = window.location.pathname;

  if (loading) return <LoadingFallback />;
  if (!user) return children;

  if (!requiresOrganizationSetup(user)) {
    console.log('[NAV]', path, '->', '/organization (organization setup already complete)');
    return <Navigate to="/organization" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { loading } = useAuth();
  const path = window.location.pathname;
  console.log('[PublicRoute]', path, { loading });

  if (loading) { console.log('[NAV]', path, '->', 'LOADING'); return <LoadingFallback />; }
  console.log('[PublicRoute]', path, '->', 'RENDER CHILDREN');
  return children;
};

/**
 * Parent portal guard + shell. Mirrors the v2 wiring (RequireParentAuth >
 * ParentShell) using the v3 ProtectedRoute guard so logged-in parents are
 * never bounced to /auth. The v2 parent components read auth.token/auth.user
 * from localStorage, which AuthContextV3 keeps in sync with the v3 session.
 */
const ParentPortal = ({ children, allowedRoles = ['parent', 'buyer'] }) => (
  <ProtectedRoute allowedRoles={allowedRoles}>
    <Suspense fallback={<LoadingFallback />}>
      <ParentShell>{children}</ParentShell>
    </Suspense>
  </ProtectedRoute>
);

/**
 * Backward compatibility: old magic links pointed to /auth/verify
 * Redirect to /auth/callback with preserved query params
 */
const VerifyRedirect = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const intent = searchParams.get('intent') || 'auth';
  const portal = searchParams.get('portal');
  const path = window.location.pathname;

  if (token) {
    const portalQuery = portal ? `&portal=${encodeURIComponent(portal)}` : '';
    console.log('[NAV]', path, '->', `/auth/callback?token=${token}&intent=${intent}${portalQuery}`);
    return <Navigate to={`/auth/callback?token=${token}&intent=${intent}${portalQuery}`} replace />;
  }
  console.log('[NAV]', path, '->', '/auth/invalid-link?reason=missing_token');
  return <Navigate to="/auth/invalid-link?reason=missing_token" replace />;
};

const AppRoutes = () => {
  const path = window.location.pathname;
  console.log('[App]', path);

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* V3 AUTH ROUTES (New Passwordless Auth) */}
        <Route path="/auth" element={<PublicRoute><UniversalEntry /></PublicRoute>} />
        <Route path="/organization/login" element={<PublicRoute><Suspense fallback={<LoadingFallback />}><OrganizationLogin /></Suspense></PublicRoute>} />
        {/* Canonical Seer / Organization Super Admin login */}
        <Route path="/superorganization/login" element={<PublicRoute><Suspense fallback={<LoadingFallback />}><OrganizationSuperAdminLoginPage /></Suspense></PublicRoute>} />
        <Route path="/organization/pending" element={<Suspense fallback={<LoadingFallback />}><OrganizationPending /></Suspense>} />
        <Route path="/organization/onboarding" element={<ProtectedRoute allowedRoles={['organization_admin']}><RedirectIfOrganizationSetupComplete><Suspense fallback={<LoadingFallback />}><OrganizationOnboarding /></Suspense></RedirectIfOrganizationSetupComplete></ProtectedRoute>} />

        {/* Canonical School Super Admin login */}
        <Route path="/superschool/login" element={<PublicRoute><Suspense fallback={<LoadingFallback />}><SuperAdminLoginPage /></Suspense></PublicRoute>} />

        {/* Legacy admin login aliases → canonical URLs (backward-compatible) */}
        <Route path="/admin/login" element={<Navigate to="/superschool/login" replace />} />
        <Route path="/superadmin/login" element={<Navigate to="/superschool/login" replace />} />
        <Route path="/organization-superadmin/login" element={<Navigate to="/superorganization/login" replace />} />
        <Route path="/auth/callback" element={<MagicLinkCallback />} />
        <Route path="/auth/invalid-link" element={<InvalidLinkPage />} />
        <Route path="/auth/verify" element={<VerifyRedirect />} />

        {/* Vendor Magic Link — ISOLATED from existing auth flow */}
        <Route path="/vendor/magic-link/verify" element={
          <Suspense fallback={<LoadingFallback />}><VendorMagicLinkVerify /></Suspense>
        } />
        <Route path="/onboarding" element={<OnboardingWizard />} />
        <Route path="/join-school" element={<JoinSchool />} />
        <Route path="/pending-school-access" element={<PendingSchoolAccess />} />

        {/* School Onboarding — full-screen, outside the normal Layout.
            Completed School Admins are redirected to /dashboard. */}
        <Route path="/school/setup" element={
          <ProtectedRoute allowedRoles={['school_admin']}>
            <RedirectIfSetupComplete>
              <SchoolOnboardingWizard />
            </RedirectIfSetupComplete>
          </ProtectedRoute>
        } />

        {/* School Portal (locked until school setup is complete) */}
        <Route path="/dashboard" element={
          <ProtectedRoute allowedRoles={['school_admin']}>
            <RequireSchoolSetup><Dashboard /></RequireSchoolSetup>
          </ProtectedRoute>
        } />
        <Route path="/campaigns" element={
          <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'super_admin']}>
            <RequireSchoolSetup><Campaigns /></RequireSchoolSetup>
          </ProtectedRoute>
        } />
        <Route path="/campaigns/new" element={
          <ProtectedRoute allowedRoles={['school_admin', 'teacher']}>
            <RequireSchoolSetup><CampaignForm /></RequireSchoolSetup>
          </ProtectedRoute>
        } />
        <Route path="/campaigns/:id" element={
          <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'super_admin']}>
            <RequireSchoolSetup><CampaignWorkspace /></RequireSchoolSetup>
          </ProtectedRoute>
        } />
        <Route path="/members" element={
          <ProtectedRoute allowedRoles={['school_admin', 'teacher']}>
            <RequireSchoolSetup><Members /></RequireSchoolSetup>
          </ProtectedRoute>
        } />
        <Route path="/verification" element={
          <ProtectedRoute allowedRoles={['school_admin']}>
            <RequireSchoolSetup><Verification /></RequireSchoolSetup>
          </ProtectedRoute>
        } />
        <Route path="/:slug/dashboard" element={
          <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'super_admin']}>
            <RequireSchoolSetup><Suspense fallback={<LoadingFallback />}><Layout><Dashboard /></Layout></Suspense></RequireSchoolSetup>
          </ProtectedRoute>
        } />

        {/* v2 slug-prefixed routes (backward compatibility for Dashboard links) */}
        <Route path="/:slug/v2/campaigns" element={
          <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'super_admin', 'organization_admin']}>
            <Suspense fallback={<LoadingFallback />}><OrganizationOrSchoolLayout><Campaigns /></OrganizationOrSchoolLayout></Suspense>
          </ProtectedRoute>
        } />
        <Route path="/:slug/v2/campaigns/new" element={
          <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'organization_admin']}>
            <Suspense fallback={<LoadingFallback />}><OrganizationOrSchoolLayout><CampaignForm /></OrganizationOrSchoolLayout></Suspense>
          </ProtectedRoute>
        } />
        <Route path="/:slug/v2/campaigns/:id" element={
          <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'super_admin', 'organization_admin']}>
            <Suspense fallback={<LoadingFallback />}><OrganizationOrSchoolLayout><CampaignWorkspace /></OrganizationOrSchoolLayout></Suspense>
          </ProtectedRoute>
        } />
        <Route path="/:slug/v2/campaigns/:id/edit" element={
          <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'organization_admin']}>
            <Suspense fallback={<LoadingFallback />}><OrganizationOrSchoolLayout><CampaignForm /></OrganizationOrSchoolLayout></Suspense>
          </ProtectedRoute>
        } />
        <Route path="/:slug/v2/campaigns/:campaignId/items/new" element={
          <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'organization_admin']}>
            <Suspense fallback={<LoadingFallback />}><OrganizationOrSchoolLayout><ItemFormV2 /></OrganizationOrSchoolLayout></Suspense>
          </ProtectedRoute>
        } />
        <Route path="/:slug/v2/campaigns/:campaignId/items/:itemId/edit" element={
          <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'organization_admin']}>
            <Suspense fallback={<LoadingFallback />}><OrganizationOrSchoolLayout><ItemFormV2 /></OrganizationOrSchoolLayout></Suspense>
          </ProtectedRoute>
        } />
        <Route path="/:slug/v2/items" element={
          <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'super_admin']}>
            <RequireSchoolSetup><Suspense fallback={<LoadingFallback />}><Layout><ItemListV2 /></Layout></Suspense></RequireSchoolSetup>
          </ProtectedRoute>
        } />
        <Route path="/:slug/orders" element={
          <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'super_admin']}>
            <RequireSchoolSetup><Suspense fallback={<LoadingFallback />}><Layout><Orders /></Layout></Suspense></RequireSchoolSetup>
          </ProtectedRoute>
        } />
        <Route path="/:slug/reports" element={
          <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'super_admin']}>
            <RequireSchoolSetup><Suspense fallback={<LoadingFallback />}><Layout><SchoolReports /></Layout></Suspense></RequireSchoolSetup>
          </ProtectedRoute>
        } />
        <Route path="/:slug/members" element={
          <ProtectedRoute allowedRoles={['school_admin', 'teacher']}>
            <RequireSchoolSetup><Suspense fallback={<LoadingFallback />}><Layout><Members /></Layout></Suspense></RequireSchoolSetup>
          </ProtectedRoute>
        } />
        <Route path="/:slug/verification" element={
          <ProtectedRoute allowedRoles={['school_admin', 'teacher', 'organization_admin']}>
            <Suspense fallback={<LoadingFallback />}><OrganizationOrSchoolLayout><Verification /></OrganizationOrSchoolLayout></Suspense>
          </ProtectedRoute>
        } />
        <Route path="/:slug/workspace/settings" element={
          <ProtectedRoute allowedRoles={['school_admin', 'super_admin', 'organization_admin', 'organization_super_admin']}>
            <Suspense fallback={<LoadingFallback />}><OrganizationOrSchoolLayout><WorkspaceSettings /></OrganizationOrSchoolLayout></Suspense>
          </ProtectedRoute>
        } />

        {/* Organisation member's workspace list (post-onboarding landing) */}
        <Route path="/my-organizations" element={
          <ProtectedRoute allowedRoles={['organization_admin', 'organization_super_admin', 'buyer', 'teacher']}>
            <Suspense fallback={<LoadingFallback />}><MyOrganizations /></Suspense>
          </ProtectedRoute>
        } />

        {/* Parent Portal (reconnected from v2 route tree) */}
        <Route path="/parent" element={<ParentPortal><ParentDashboard /></ParentPortal>} />
        <Route path="/parent/dashboard" element={<ParentPortal><ParentDashboard /></ParentPortal>} />
        <Route path="/parent/orders" element={<ParentPortal><ParentOrders /></ParentPortal>} />
        <Route path="/parent/product/:productId" element={<ParentPortal><ParentProductDetail /></ParentPortal>} />
        <Route path="/parent/product/:productId/order" element={<ParentPortal><ParentOrder /></ParentPortal>} />
        <Route path="/parent/cart" element={<ParentPortal><ParentCart /></ParentPortal>} />
        <Route path="/parent/cart/receipt/:cartId" element={<ParentPortal><ParentCartReceipt /></ParentPortal>} />
        <Route path="/parent/order/:orderId" element={<ParentPortal><ParentOrderEdit /></ParentPortal>} />
        <Route path="/parent/receipt/:orderId" element={<ParentPortal><ParentReceipt /></ParentPortal>} />
        <Route path="/parent/campaigns" element={<ParentPortal><ParentCampaigns /></ParentPortal>} />
        <Route path="/parent/profile" element={<ParentPortal><ParentProfile /></ParentPortal>} />
        <Route path="/parent/tasks" element={<ParentPortal><ParentTasks /></ParentPortal>} />
        <Route path="/parent/login" element={<Navigate to="/auth" replace />} />
        <Route path="/payment/callback" element={<PaymentCallback />} />
        <Route path="/explore" element={<Suspense fallback={<LoadingFallback />}><ExplorePage /></Suspense>} />

        {/* Vendor Portal */}
        <Route path="/vendor" element={
          <VendorProtectedRoute>
            <Suspense fallback={<LoadingFallback />}><VendorShell /></Suspense>
          </VendorProtectedRoute>
        }>
          <Route index element={<Suspense fallback={<LoadingFallback />}><VendorDashboardPage /></Suspense>} />
          <Route path="rfqs-open" element={<Suspense fallback={<LoadingFallback />}><VendorRFQsOpen /></Suspense>} />
          <Route path="submitted-quotations" element={<Suspense fallback={<LoadingFallback />}><VendorSubmittedQuotations /></Suspense>} />
          <Route path="awarded-jobs" element={<Suspense fallback={<LoadingFallback />}><VendorAwardedJobs /></Suspense>} />
          <Route path="production" element={<Suspense fallback={<LoadingFallback />}><VendorProduction /></Suspense>} />
          <Route path="printing" element={<Suspense fallback={<LoadingFallback />}><VendorPrinting /></Suspense>} />
          <Route path="completed" element={<Suspense fallback={<LoadingFallback />}><VendorCompleted /></Suspense>} />
          <Route path="profile" element={<Suspense fallback={<LoadingFallback />}><VendorProfile /></Suspense>} />
        </Route>

        {/* Legacy admin paths — redirect to the correct ecosystem root (role-aware) */}
        <Route path="/admin/*" element={<LegacyAdminRedirect from="/admin" />} />
        <Route path="/organization-superadmin/*" element={<LegacyAdminRedirect from="/organization-superadmin" />} />

        {/* School Super Admin Portal — SCHOOL ecosystem only */}
        <Route path="/superschool" element={
          <ProtectedRoute allowedRoles={['super_admin', 'platform_admin', 'admin']}>
            <Suspense fallback={<LoadingFallback />}><SchoolPlatformShell /></Suspense>
          </ProtectedRoute>
        }>
          <Route index element={<Suspense fallback={<LoadingFallback />}><SuperAdminDashboard /></Suspense>} />
          <Route path="approvals" element={<Suspense fallback={<LoadingFallback />}><SPApprovals /></Suspense>} />
          <Route path="schools" element={<Suspense fallback={<LoadingFallback />}><SPSchools /></Suspense>} />
          <Route path="schools/:id" element={<Suspense fallback={<LoadingFallback />}><SPSchoolDetail /></Suspense>} />
          <Route path="school-admins" element={<Suspense fallback={<LoadingFallback />}><SPSchoolAdmins /></Suspense>} />
          <Route path="parents" element={<Suspense fallback={<LoadingFallback />}><SPParents /></Suspense>} />
          <Route path="campaigns" element={<Suspense fallback={<LoadingFallback />}><SPCampaigns /></Suspense>} />
          <Route path="campaigns/:id/review" element={<Suspense fallback={<LoadingFallback />}><SPCampaignReview /></Suspense>} />
          <Route path="orders" element={<Suspense fallback={<LoadingFallback />}><SPOrders /></Suspense>} />
          <Route path="print-labels" element={<Suspense fallback={<LoadingFallback />}><SPPrintLabels /></Suspense>} />
          <Route path="payments" element={<Suspense fallback={<LoadingFallback />}><SPPayments /></Suspense>} />
          <Route path="reports" element={<Suspense fallback={<LoadingFallback />}><SPReports /></Suspense>} />
          <Route path="audit-logs" element={<Suspense fallback={<LoadingFallback />}><SPAuditLogs /></Suspense>} />
          <Route path="notifications" element={<Suspense fallback={<LoadingFallback />}><SPNotifications /></Suspense>} />
          <Route path="settings" element={<Suspense fallback={<LoadingFallback />}><SPSettings /></Suspense>} />

          {/* Procurement — owned by Super Admin */}
          <Route path="procurement/dashboard" element={<Suspense fallback={<LoadingFallback />}><ProcurementDashboard /></Suspense>} />
          <Route path="procurement/rfqs" element={<Suspense fallback={<LoadingFallback />}><ProcurementRFQs /></Suspense>} />
          <Route path="procurement/vendors" element={<Suspense fallback={<LoadingFallback />}><ProcurementVendors /></Suspense>} />
          <Route path="procurement/quotations" element={<Suspense fallback={<LoadingFallback />}><ProcurementQuotations /></Suspense>} />
          <Route path="procurement/awarded" element={<Suspense fallback={<LoadingFallback />}><ProcurementAwardedJobs /></Suspense>} />
          <Route path="procurement/production" element={<Suspense fallback={<LoadingFallback />}><ProcurementProduction /></Suspense>} />
          <Route path="procurement/printing" element={<Suspense fallback={<LoadingFallback />}><ProcurementPrinting /></Suspense>} />
         <Route path="procurement/completed" element={<Suspense fallback={<LoadingFallback />}><ProcurementCompleted /></Suspense>} />
         </Route>

         {/* Organisation Super Admin Portal — ORGANIZATION ecosystem only */}
         <Route path="/superorganization" element={
           <ProtectedRoute allowedRoles={['organization_super_admin', 'platform_admin', 'admin']}>
             <Suspense fallback={<LoadingFallback />}><OrganizationSuperAdminShell /></Suspense>
           </ProtectedRoute>
         }>
           <Route index element={<Suspense fallback={<LoadingFallback />}><SuperAdminDashboard /></Suspense>} />
           <Route path="organizations" element={<Suspense fallback={<LoadingFallback />}><SchoolPlatformOrganizations /></Suspense>} />
           <Route path="organizations/:id" element={<Suspense fallback={<LoadingFallback />}><SPSchoolDetail /></Suspense>} />
           <Route path="approvals" element={<Suspense fallback={<LoadingFallback />}><SPApprovals /></Suspense>} />
           <Route path="campaigns" element={<Suspense fallback={<LoadingFallback />}><SPCampaigns /></Suspense>} />
           <Route path="campaigns/:id/review" element={<Suspense fallback={<LoadingFallback />}><SPCampaignReview /></Suspense>} />
           <Route path="admins" element={<Suspense fallback={<LoadingFallback />}><SPSchoolAdmins /></Suspense>} />
           <Route path="members" element={<Suspense fallback={<LoadingFallback />}><SPParents /></Suspense>} />
           <Route path="orders" element={<Suspense fallback={<LoadingFallback />}><SPOrders /></Suspense>} />
           <Route path="print-labels" element={<Suspense fallback={<LoadingFallback />}><SPPrintLabels /></Suspense>} />
           <Route path="payments" element={<Suspense fallback={<LoadingFallback />}><SPPayments /></Suspense>} />
           <Route path="reports" element={<Suspense fallback={<LoadingFallback />}><SPReports /></Suspense>} />
           <Route path="audit-logs" element={<Suspense fallback={<LoadingFallback />}><SPAuditLogs /></Suspense>} />
           <Route path="notifications" element={<Suspense fallback={<LoadingFallback />}><SPNotifications /></Suspense>} />
           <Route path="settings" element={<Suspense fallback={<LoadingFallback />}><SPSettings /></Suspense>} />

           {/* Procurement subroutes for Organisation Super Admin */}
           <Route path="procurement/dashboard" element={<Suspense fallback={<LoadingFallback />}><ProcurementDashboard /></Suspense>} />
           <Route path="procurement/rfqs" element={<Suspense fallback={<LoadingFallback />}><ProcurementRFQs /></Suspense>} />
           <Route path="procurement/vendors" element={<Suspense fallback={<LoadingFallback />}><ProcurementVendors /></Suspense>} />
           <Route path="procurement/quotations" element={<Suspense fallback={<LoadingFallback />}><ProcurementQuotations /></Suspense>} />
           <Route path="procurement/awarded" element={<Suspense fallback={<LoadingFallback />}><ProcurementAwardedJobs /></Suspense>} />
           <Route path="procurement/production" element={<Suspense fallback={<LoadingFallback />}><ProcurementProduction /></Suspense>} />
           <Route path="procurement/printing" element={<Suspense fallback={<LoadingFallback />}><ProcurementPrinting /></Suspense>} />
           <Route path="procurement/completed" element={<Suspense fallback={<LoadingFallback />}><ProcurementCompleted /></Suspense>} />
         </Route>

         {/* Organisation / Club Portal — canonical slug-scoped URLs.
             /organization/{slug}/... resolves the organisation from the URL
             slug (primary context); /organization remains the compatibility
             landing (single org → redirect, multi org → selection). */}
         <Route path="/organization" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <RequireOrganizationSetup><Suspense fallback={<LoadingFallback />}><OrganizationLanding /></Suspense></RequireOrganizationSetup>
           </ProtectedRoute>
         } />
         <Route path="/organization/:slug" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <RequireOrganizationSetup><Suspense fallback={<LoadingFallback />}><OrganizationLayout><OrganizationDashboard /></OrganizationLayout></Suspense></RequireOrganizationSetup>
           </ProtectedRoute>
         } />
         <Route path="/organization/:slug/dashboard" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <RequireOrganizationSetup><Suspense fallback={<LoadingFallback />}><OrganizationLayout><OrganizationDashboard /></OrganizationLayout></Suspense></RequireOrganizationSetup>
           </ProtectedRoute>
         } />
         <Route path="/organization/:slug/campaigns" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <RequireOrganizationSetup><Suspense fallback={<LoadingFallback />}><OrganizationLayout><Campaigns /></OrganizationLayout></Suspense></RequireOrganizationSetup>
           </ProtectedRoute>
         } />
         <Route path="/organization/:slug/campaigns/new" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <RequireOrganizationSetup><Suspense fallback={<LoadingFallback />}><OrganizationLayout><CampaignForm /></OrganizationLayout></Suspense></RequireOrganizationSetup>
           </ProtectedRoute>
         } />
         <Route path="/organization/:slug/campaigns/:id" element={
           <ProtectedRoute allowedRoles={['organization_admin', 'organization_super_admin']}>
             <RequireOrganizationSetup><Suspense fallback={<LoadingFallback />}><OrganizationLayout><CampaignWorkspace /></OrganizationLayout></Suspense></RequireOrganizationSetup>
           </ProtectedRoute>
         } />
         <Route path="/organization/:slug/campaigns/:id/edit" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <RequireOrganizationSetup><Suspense fallback={<LoadingFallback />}><OrganizationLayout><CampaignForm /></OrganizationLayout></Suspense></RequireOrganizationSetup>
           </ProtectedRoute>
         } />
         <Route path="/organization/:slug/members" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <RequireOrganizationSetup><Suspense fallback={<LoadingFallback />}><OrganizationLayout><OrganizationMembers /></OrganizationLayout></Suspense></RequireOrganizationSetup>
           </ProtectedRoute>
         } />
         <Route path="/organization/:slug/verification" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <RequireOrganizationSetup><Suspense fallback={<LoadingFallback />}><OrganizationLayout><Verification /></OrganizationLayout></Suspense></RequireOrganizationSetup>
           </ProtectedRoute>
         } />
         <Route path="/organization/:slug/orders" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <RequireOrganizationSetup><Suspense fallback={<LoadingFallback />}><OrganizationLayout><OrganizationOrders /></OrganizationLayout></Suspense></RequireOrganizationSetup>
           </ProtectedRoute>
         } />
         <Route path="/organization/:slug/reports" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <RequireOrganizationSetup><Suspense fallback={<LoadingFallback />}><OrganizationLayout><OrganizationReports /></OrganizationLayout></Suspense></RequireOrganizationSetup>
           </ProtectedRoute>
         } />
         <Route path="/organization/:slug/settings" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <RequireOrganizationSetup><Suspense fallback={<LoadingFallback />}><OrganizationLayout><OrganizationSettings /></OrganizationLayout></Suspense></RequireOrganizationSetup>
           </ProtectedRoute>
         } />

         {/* Legacy generic organisation URLs → resolve to the canonical slug
             URL (single org) or the selection page (multi org). */}
         <Route path="/organization/campaigns" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <Suspense fallback={<LoadingFallback />}><OrganizationLanding /></Suspense>
           </ProtectedRoute>
         } />
         <Route path="/organization/campaigns/new" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <Suspense fallback={<LoadingFallback />}><OrganizationLanding /></Suspense>
           </ProtectedRoute>
         } />
         <Route path="/organization/campaigns/:id" element={
           <ProtectedRoute allowedRoles={['organization_admin', 'organization_super_admin']}>
             <Suspense fallback={<LoadingFallback />}><OrganizationLanding /></Suspense>
           </ProtectedRoute>
         } />
         <Route path="/organization/campaigns/:id/edit" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <Suspense fallback={<LoadingFallback />}><OrganizationLanding /></Suspense>
           </ProtectedRoute>
         } />
         <Route path="/organization/members" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <Suspense fallback={<LoadingFallback />}><OrganizationLanding /></Suspense>
           </ProtectedRoute>
         } />
         <Route path="/organization/verification" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <Suspense fallback={<LoadingFallback />}><OrganizationLanding /></Suspense>
           </ProtectedRoute>
         } />
         <Route path="/organization/orders" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <Suspense fallback={<LoadingFallback />}><OrganizationLanding /></Suspense>
           </ProtectedRoute>
         } />
         <Route path="/organization/reports" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <Suspense fallback={<LoadingFallback />}><OrganizationLanding /></Suspense>
           </ProtectedRoute>
         } />
         <Route path="/organization/settings" element={
           <ProtectedRoute allowedRoles={['organization_admin']}>
             <Suspense fallback={<LoadingFallback />}><OrganizationLanding /></Suspense>
           </ProtectedRoute>
         } />

      {/* Public Campaign Preview & Short Links */}
      <Route path="/public/:slug/campaigns/:id" element={
        <Suspense fallback={null}>
          <PublicCampaignPage />
        </Suspense>
      } />
      <Route path="/c/:id" element={
        <Suspense fallback={null}>
          <PublicCampaignPage />
        </Suspense>
      } />
      <Route path="/:slug/c/:id" element={
        <Suspense fallback={null}>
          <PublicCampaignPage />
        </Suspense>
      } />

      {/* Unauthorized — explicit route so it never falls to catch-all */}
      <Route path="/unauthorized" element={
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-6 sm:p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-6">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
            <p className="text-slate-500 text-sm sm:text-base mb-6">
              Your account role does not have permission to access this page.
            </p>
            <button
              onClick={() => window.location.href = '/auth'}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Back to Login
            </button>
          </div>
        </div>
      } />

      {/* Public routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/landing" element={<LandingPage />} />

      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/auth" replace />} />
      </Routes>
    </Suspense>
  );
};

const App = () => {
  useEffect(() => {
    const splash = document.getElementById('splash');
    if (splash) {
      splash.style.opacity = '0';
      setTimeout(() => {
        splash.remove();
      }, 300);
    }
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <NotificationProvider>
            <AppRoutes />
          </NotificationProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;
